import { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableContainer,
  SimpleTableData,
  SimpleTableHead,
  SimpleTableHeader,
  SimpleTableRow,
  SimpleTableWrapper,
  Checkbox,
  CHECKBOX_SIZES,
  INPUT_SIZES,
  SearchInput,
  IconInfo,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES,
} from 'cdk-radial';
import styled from 'styled-components';
import CMFSelectorDropdown from './CMFSelectorDropdown';
import { ACTIONS } from '../../utils/daabSubscriptions';
import {
  CMF_STATUS,
} from '../../../../constants/constants';
import './DAABSubscriptions.scss';
import { NoDataOverlay } from '../../utils/NoDataOverlay';

const getApiScopeKey = (apiId, apiType) => `${apiId || ''}::${apiType || ''}`;

const StyledSimpleTableRow = styled(SimpleTableRow)`
  height: 32px !important; // Force height for the header row
`;

const StyledSimpleTableHeader = styled(SimpleTableHeader)`
  position: sticky;
  top: 0;
  height: 32px !important;
  vertical-align: middle;
`;

const StickySimpleTable = styled(SimpleTable)`
  position: relative;
  overflow: auto;
`;

const StickySimpleTableContainer = styled(SimpleTableContainer)`
  height: 400px;
  overflow: auto;
`;

const ZebraSimpleTableRow = styled(SimpleTableRow)`
  :nth-child(even) {
    background-color: ${({ theme }) =>
      theme.color.zebraStripe.background.value};
    border-top: 1px solid ${({ theme }) => theme.color.gray[200].value};
    :hover {
      background-color: ${({ theme }) => theme.color.zebraStripe.hover.value};
    }
  }
  &:last-child {
    border-bottom: none;
  }
`;

const StyledIconInfo = styled(IconInfo)`
  fill: #151b25b2;
  padding: 4px;
`;

const getSubscriptionEntityId = (item) => item?.orgId || item?.entityId;

export function processLocations(inputData = [], isRemoveLocations) {
  const filteredData = !isRemoveLocations
    ? inputData.filter((e) => e?.status !== CMF_STATUS.INACTIVE)
    : inputData;

  const groupedStores = {};

  for (const store of filteredData) {
    const entityId = store?.entityId;
    if (!entityId) continue;
    
    const cmfValues = Array.isArray(store.cmfNumber)
      ? store.cmfNumber.map((c) => c && c.toString())
      : [store.cmfNumber && store.cmfNumber.toString()];

    if (!groupedStores[entityId]) {
      groupedStores[entityId] = { ...store, cmfNumber: [] };
    }
    cmfValues.forEach((val) => {
      if (val && !groupedStores[entityId].cmfNumber.includes(val)) {
        groupedStores[entityId].cmfNumber.push(val);
      }
    });
  }
  
  return Object.values(groupedStores);
}

export const LocationsTable = ({
  handleLocationSelection,
  isRemoveLocations,
  handleRemoveLocation,
  bundleLocations,
  selectedApiToManageLocation,
  bundleId,
  activeSubByEntityId = {},
  useApiScopedStatus = false,
}) => {
  const state = useSelector((state) => state);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const allLocations = processLocations(
    state?.loginDetailsSlice?.allLocationsWithoutMergingCmfs || [],
    isRemoveLocations
  );
  const [value, setValue] = useState('');
  const { subscriptions, subscribeRequest } = state?.daabSubscriptionsSlice;
  const currentApiScopeKey = getApiScopeKey(
    selectedApiToManageLocation?.id,
    selectedApiToManageLocation?.type
  );
  const scopedSubscriptions = Array.isArray(subscriptions)
    ? subscriptions.filter((sub) => {
        if (!currentApiScopeKey || currentApiScopeKey === '::') {
          return true;
        }

        return (
          !sub?.optimisticApiScopeKey ||
          sub.optimisticApiScopeKey === currentApiScopeKey
        );
      })
    : [];
  let subByEntityId = Object.fromEntries(
    scopedSubscriptions
      .map((sub) => [getSubscriptionEntityId(sub), sub])
      .filter(([entityId]) => Boolean(entityId))
  );

  const scopedSubByEntityId = useApiScopedStatus
    ? activeSubByEntityId || {}
    : subByEntityId;
  if (isRemoveLocations) {
    subByEntityId = Object.fromEntries(
      Object.entries(subByEntityId).filter(
        ([_, value]) =>
          value.status === 'active' ||
          value.status === 'pending' ||
          value.status === 'enabled'
      )
    );
  }

  const [selectedRows, setSelectedRows] = useState(() => {
    const initialSelected = new Set(Object.keys(subscribeRequest));
    return initialSelected;
  });
  const [selectedCmf, setSelectedCmf] = useState(() => {
    return Object.fromEntries(
      Object.entries(subscribeRequest).map(([key, value]) => [
        key,
        value.cmfNumber,
      ])
    );
  });
  const bundleLocationsCmfKey = useMemo(() => {
    if (!bundleLocations || bundleLocations.length === 0) return '';
    return bundleLocations
      .filter(loc => loc.orgId && loc.cmf)
      .map(loc => `${loc.orgId}:${loc.cmf}`)
      .sort()
      .join('|');
  }, [bundleLocations]);
  const appliedBundleLocationsRef = useRef('');
  useEffect(() => {
    if (bundleLocationsCmfKey && bundleLocationsCmfKey !== appliedBundleLocationsRef.current) {
      appliedBundleLocationsRef.current = bundleLocationsCmfKey;
      
      setSelectedCmf((prevCmf) => {
        const updatedCmf = { ...prevCmf };
        bundleLocations.forEach((loc) => {
          if (loc.orgId && loc.cmf) {
            updatedCmf[loc.orgId] = loc.cmf;
          }
        });
        return updatedCmf;
      });
    }
  }, [bundleLocationsCmfKey, bundleLocations]);

  useEffect(() => {
    if (bundleId) {
      const bundleLocationsArray = Array.isArray(bundleLocations) ? bundleLocations : [];
      const filteredBundleLocations = bundleLocationsArray.filter(
        (bl) => bl?.bundleId === bundleId
      );
      const bundleLocationIds = new Set(
        filteredBundleLocations.map((bl) => bl?.orgId).filter(Boolean)
      );

      const tableData =
        Array.isArray(allLocations) &&
        allLocations
          .filter((loc) => bundleLocationIds.has(loc.entityId))
          .filter((loc) => {
            if (isRemoveLocations) {
              return subByEntityId.hasOwnProperty(loc?.entityId);
            }
            const status = scopedSubByEntityId?.[loc.entityId]?.status;
            return status === 'inactive' || status === 'disabled' || !status;
          })
          .map((location) => {
            const bundleLocation = filteredBundleLocations.find(
              (bl) => bl?.orgId === location.entityId
            );

            const apiScopedStatus = scopedSubByEntityId?.[location.entityId]?.status;
            const isAlreadySubscribed = 
              apiScopedStatus && 
              apiScopedStatus !== 'inactive' && 
              apiScopedStatus !== 'disabled';

            return {
              id: location.entityId,
              location: location?.partyName,
              address:
                location?.addresses[0] &&
                location?.addresses[0].streetAddress +
                  ', ' +
                  location?.addresses[0].city,
              isRowSelected: isRemoveLocations
                ? selectedRows.has(location.entityId)
                : selectedRows.has(location.entityId) || isAlreadySubscribed,
              isDisabled: isRemoveLocations ? false : isAlreadySubscribed,
              cmf: isRemoveLocations
                ? [
                    {
                      label: subByEntityId[location?.entityId]?.cmf,
                      value: subByEntityId[location?.entityId]?.cmf,
                    },
                  ]
                : bundleLocation
                ? [{ label: bundleLocation.cmf, value: bundleLocation.cmf }]
                : location.cmfNumber.map((cmf) => ({ label: cmf, value: cmf })),
              storeId: location.storeId,
              isFromBundle: true,
            };
          });
      setData(tableData);
      setFilteredData(tableData);
      return;
    }
    const tableData =
      Array.isArray(allLocations) &&
      allLocations
        .filter((loc) => {
          if (isRemoveLocations) {
            return subByEntityId.hasOwnProperty(loc?.entityId);
          } else {
            const status = scopedSubByEntityId?.[loc.entityId]?.status;
            return status === 'inactive' || status === 'disabled' || !status;
          }
        })
        ?.map((location, index) => {
          const currentBundleId = selectedApiToManageLocation?.bundleId || selectedApiToManageLocation?.id;
          const bundleLocation = bundleLocations?.find(
            (bl) =>
              bl.orgId === location.entityId &&
              bl.bundleId === currentBundleId
          );

          return {
            id: location.entityId,
            location: location?.partyName,
            address:
              location?.addresses[0] &&
              location?.addresses[0].streetAddress +
                ', ' +
                location?.addresses[0].city,
            isRowSelected: selectedRows.has(location.entityId),
            cmf: isRemoveLocations
              ? [
                  {
                    label: subByEntityId[location?.entityId]?.cmf,
                    value: subByEntityId[location?.entityId]?.cmf,
                  },
                ]
              : bundleLocation
              ? [{ label: bundleLocation.cmf, value: bundleLocation.cmf }] // Use bundle CMF if available
              : location.cmfNumber.map((cmf) => ({ label: cmf, value: cmf })),
            storeId: location.storeId,
            isFromBundle: !!bundleLocation,
          };
        });
    setData(tableData);
    setFilteredData(tableData);
  }, [
    state?.loginDetailsSlice?.allLocationsWithoutMergingCmfs,
    selectedRows,
    bundleLocationsCmfKey,
    bundleId,
    isRemoveLocations,
    selectedApiToManageLocation?.bundleId,
    selectedApiToManageLocation?.id,
  ]);
  useEffect(() => {
    if (!handleRemoveLocation) return;
    
    const selectedLocations = data.filter((location) =>
      selectedRows.has(location.id)
    );

    const locationDataArray = selectedLocations.map((location) => ({
      location: location.location,
      entityId: location.id,
      address: location.address,
      cmfNumber: subByEntityId[location.id]?.cmf,
      storeId: location.storeId,
    }));

    if (handleRemoveLocation) {
      handleRemoveLocation(locationDataArray);
    }
  }, [selectedRows, data, selectedCmf]);

  useEffect(() => {
    if (!value) {
      setFilteredData(data);
    } else {
      const lowercasedValue = value.toLowerCase();
      const filtered = data.filter(
        (row) =>
          row.location?.toLowerCase().includes(lowercasedValue) ||
          row.address?.toLowerCase().includes(lowercasedValue) ||
          row.cmf?.some((item) =>
            item?.value?.toLowerCase().includes(lowercasedValue)
          )
      );
      setFilteredData(filtered);
    }
  }, [value, data]);

  const handleRowClicked = async (selectedRow) => {
    if (selectedRow.isDisabled) {
      return;
    }

    const { id, address, location, cmf, storeId } = selectedRow;
    const cmfNumber = cmf.length === 1 ? cmf[0].value : selectedCmf[id];
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
        if (handleLocationSelection) {
          handleLocationSelection({
            locationData: { entityId: id, address, location },
            action: ACTIONS.REMOVE_LOCATION,
          });
        }
      } else {
        newSet.add(id);
        if (handleLocationSelection) {
          handleLocationSelection({
            locationData: {
              entityId: id,
              cmfNumber,
              storeId,
              address,
              location,
            },
            action: ACTIONS.ADD_LOCATION,
          });
        }
      }
      return newSet;
    });
  };

  const handleCmfSelect = (rowId, option, storeId) => {
    setSelectedCmf((prev) => ({
      ...prev,
      [rowId]: option?.value,
    }));
    handleLocationSelection({
      locationData: { entityId: rowId, storeId },
      action: ACTIONS.UPDATE_CMF,
      cmf: option?.value,
    });
  };

  return (
    <>
      <div className="manage-locations-grid">
        <div className="subheader-search-wrapper">
          <div className="add-locations-subheader">
            {isRemoveLocations
              ? 'Deactivate the locations of your Dealerships from this API.'
              : 'Select the dealership locations where you would like to use this API.'}
          </div>
          <SearchInput
            className="manage-location-search-input"
            clearButtonLabel="clearButtonLabel"
            data-testId="location-search-input"
            id="location-search-input"
            isAlwaysExpanded
            name="location-search-input"
            onChange={(input) => setValue(input)}
            placeholder="Search locations by name or CMF number"
            size={INPUT_SIZES.STANDARD}
            value={value}
          />
        </div>
        <SimpleTableWrapper data-testid="simple-table-wrapper">
          <StickySimpleTableContainer className="manage-locations-table-container">
            <StickySimpleTable aria-label="Table">
              <SimpleTableHead>
                <StyledSimpleTableRow className="manage-locations-table-header">
                  <StyledSimpleTableHeader
                    style={{ width: '1%' }}
                    className="manage-locations-table-toggle-column"
                  ></StyledSimpleTableHeader>
                  <StyledSimpleTableHeader
                    style={{ width: '28%' }}
                  >{`Dealership Location (${data?.length})`}</StyledSimpleTableHeader>
                  <StyledSimpleTableHeader style={{ width: '30%' }}>
                    Address
                  </StyledSimpleTableHeader>
                  <StyledSimpleTableHeader style={{ width: '20%' }}>
                    CMF Number
                  </StyledSimpleTableHeader>
                </StyledSimpleTableRow>
              </SimpleTableHead>
              <SimpleTableBody className="manage-locations-table-body">
                {filteredData.length > 0 ? (
                  filteredData.map((row) => (
                    <ZebraSimpleTableRow
                      key={row.id}
                      isSelected={row.isSelected}
                      className="manage-locations-table-rows"
                    >
                      <SimpleTableData style={{ width: '1%' }}>
                        <Checkbox
                          name={`location-checkbox-${row.id}`}
                          data-testid={`location-checkbox-${row.id}`}
                          checked={row.isRowSelected}
                          onChange={() => handleRowClicked(row)}
                          isDisabled={row.isDisabled}
                          size={CHECKBOX_SIZES.STANDARD}
                        />
                      </SimpleTableData>
                      <SimpleTableData
                        style={{
                          width: '30%',
                          fontWeight: '500',
                          fontSize: '13px',
                          lineHeight: '20px',
                        }}
                      >
                        {row?.location}
                      </SimpleTableData>
                      <SimpleTableData
                        style={{
                          width: '30%',
                          fontSize: '13px',
                          lineHeight: '20px',
                          color: '#151B25',
                        }}
                      >
                        {row?.address}
                      </SimpleTableData>
                      <SimpleTableData
                        style={{
                          width: '18%',
                          fontSize: '13px',
                          lineHeight: '20px',
                          color: '#151B25',
                        }}
                      >
                        {Array.isArray(row.cmf) && row.cmf.length === 1 ? (
                          row.cmf[0].value
                        ) : (
                          <div className="cmf-dropdown-tooltip-wrapper">
                            <CMFSelectorDropdown
                              inputId={row.id}
                              options={row.cmf}
                              storeId={row.storeId}
                              isDisabled={!row.isRowSelected}
                              value={selectedCmf[row.id]}
                              handleCmfSelect={handleCmfSelect}
                            />
                            {!isRemoveLocations && <CMFToolTip />}
                          </div>
                        )}
                      </SimpleTableData>
                    </ZebraSimpleTableRow>
                  ))
                ) : (
                  <StyledSimpleTableRow>
                    <SimpleTableData
                      colSpan={4}
                      style={{
                        textAlign: 'center',
                        color: '#151B25B3',
                        backgroundColor: '#FFFFFF',
                      }}
                    >
                      <NoDataOverlay message={`No locations to show.`} />
                    </SimpleTableData>
                  </StyledSimpleTableRow>
                )}
              </SimpleTableBody>
              <div></div>
            </StickySimpleTable>
          </StickySimpleTableContainer>
        </SimpleTableWrapper>
      </div>
    </>
  );
};
LocationsTable.defaultProps = {
  handleLocationSelection: () => {},
  handleRemoveLocation: () => {},
  isRemoveLocations: false,
};

const CMFToolTip = () => {
  const tooltipProps = {
    id: 'cmf-tooltip',
    text: "If you are not sure of the correct CMF #, speak with your Dealer's Enterprise Administrator or contact CDK Customer Care at 866.668.5394, option 8",
    theme: THEMES.LIGHT,
    styles: { zIndex: 1001 },
  };
  const ComponentToRender = ({ ...props }) => {
    return <StyledIconInfo {...props} />;
  };
  const ComponentWithTooltip = withTooltip(ComponentToRender, tooltipProps);

  return (
    <ComponentWithTooltip
      horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.LEFT}
      marginAroundElement={8}
      verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.TOP}
    />
  );
};
